import React, { useCallback, useRef, useEffect, useState, useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import ReactGA from "react-ga4";
import { Spin, Button } from "antd";
import Tabs, { TabData } from "src/shared/components/Tabs";

import styles from "./index.module.scss";
import TopNavbar, { MenuItem } from "src/shared/components/Navbar/NewNavbar";
import NumberScroller from "src/shared/components/NumberScroller";
import { useRedux } from "src/shared/hooks";
import { changeTimeTabAppWide, fetchFiltersConfig, FiltersConfigPayload } from "src/store/app";
import { REFRESH_ICON_ANIMATION } from "src/shared/config";
import { TabNames } from "src/api/common";
import {
  fetchCategory,
  setHeader,
  searchCategory,
  searchReset,
  setCategoriesFilters,
  FetchCategoryPayload,
  SearchCategoryPayload,
} from "src/store/category";
import { setFilters, clearDetails } from "src/store/details";
import Search from "../../shared/components/Search";
import CategoriesModel from "src/shared/models/CategoriesModel";
import MetricModel from "src/shared/models/MetricModel";
import { IRootState } from "src/shared/types";
import { IReduxOperations, IActionFactory } from "src/store";
import styled from "@emotion/styled";
import colors from "src/styles/variables.scss";
import { SyncOutlined } from "src/icons";
import { useEffectOnPropsChange, reduxOperationToStatus, safeGet } from "src/shared/utils";
import FiltersPane from "src/shared/components/FiltersPanev2";
import { animated, useSpring } from "react-spring";

type OptionsType = {
  className: string;
  iconClass: string;
  handleClick(): void;
};
type OptionGroupType = Array<OptionsType>;

interface IState {
  category: IReduxOperations<CategoriesModel>;
  search: IReduxOperations<Array<MetricModel>>;
  filters: IObject<any>;
  header: string;
  filtersConfig: IReduxOperations<any>;
  selectedTab: any;
}
type MappedActionProps = {
  fetchCategory(payload: FetchCategoryPayload);
  setHeader: IActionFactory<symbol, string>;
  searchCategory(payload: SearchCategoryPayload);
  searchReset: IActionFactory<symbol, unknown>;
  setCategoriesFilters: IActionFactory<symbol, IObject<any>>;
  setFilters: IActionFactory<symbol, IObject<any>>;
  clearDetails: IActionFactory<symbol, any>;
  fetchFiltersConfig(payload?: FiltersConfigPayload);
  changeTimeTabAppWide: IActionFactory<symbol, string>;
};

const RefreshBadge = styled(animated.button)`
  border: none;
  display: flex;
  align-items: center;
  border-radius: 16px;
  background: transparent;
  color: ${colors.paleYellow};
  font-size: 12px;
  width: fit-content;
  line-height: 23px;
  margin-top: 3px;
  position: relative;
  outline: none;
  opacity: 0.6;
  > span {
    margin-right: 5px;
  }
  padding: 0 10px;
`;

const IconHolder = styled.div`
  font-size: 16px;
  line-height: 13px;
`;

const CALL_DELAY = 1500;
const Category: React.FC<RouteComponentProps> = ({ history, match }) => {
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname, title: "Category" });
  }, []);

  const [showSearch, toggleSearch] = useState(false);
  const [showFiltersPane, setFiltersPane] = useState(false);

  const {
    setFilters: setL3Fiters,
    clearDetails: clearL3Data,
    ...mappedProps
  } = useRedux<IRootState, IState, MappedActionProps>(
    (state) => ({
      header: state.category.header,
      chartsDataPending: state.category.chartsDataPending,
      category: state.category.metrics,
      search: state.category.search,
      filters: state.category.filters,
      filtersConfig: state.app.filtersConfig,
      selectedTab: state.app.selectedTab,
    }),
    {
      fetchCategory,
      setHeader,
      searchCategory,
      searchReset,
      setCategoriesFilters,
      setFilters,
      clearDetails,
      fetchFiltersConfig,
      changeTimeTabAppWide,
    }
  );

  useEffect(() => {
    mappedProps.setHeader(safeGet(match, "params.name"));
    setL3Fiters({});
    clearL3Data();
    // if (!mappedProps.filtersConfig.payload) {
    mappedProps.fetchFiltersConfig();
    // }
  }, []);

  useEffect(() => {
    mappedProps.fetchCategory({
      category: safeGet(match, "params.name"),
      filters: mappedProps.filters,
    });
  }, []);

  const onSearchClick = useCallback(() => {
    mappedProps.searchReset();
    toggleSearch(true);
  }, []);

  const onBack = useCallback(() => {
    history.goBack();
  }, [history]);

  useEffectOnPropsChange(() => {
    let anomalyCount = 0,
      failureCount = 0;

    const categoryData = safeGet(mappedProps, "category.payload.data", []);
    const groups = Array.isArray(categoryData) ? categoryData : [];
    groups.forEach((group) => {
      const metrics = Array.isArray(group?.metrics) ? group.metrics : [];
      metrics.forEach((metric) => {
        if (!metric.successful) {
          failureCount++;
          ReactGA.event({
            category: "Couldn't fetch Metric",
            label: `L2:${group.id}: ${metric.label}`,
            action: "Couldn't fetch on L2",
          });
        } else if (metric.isAnomalous) {
          anomalyCount++;
          ReactGA.event({
            category: metric.isAnomalous === 2 ? "Anomalous: Threshold Breach" : "Anomalous Data",
            label: `L2:${group.id}: ${metric.label}`,
            action: `Anomalous on L2: ${safeGet(match, "params.name")}`,
          });
        } else if (metric.isStale) {
          anomalyCount++;
          ReactGA.event({
            category: "Stale Metric",
            label: `L2:${group.id}: ${metric.label}`,
            action: `Stale on L2: ${safeGet(match, "params.name")}`,
          });
        }
      });
    });

    ReactGA.event({
      category: "Count of Anomalous Metrics L2",
      label: `L2`,
      action: "Anomalous on L2",
      value: anomalyCount,
    });
    ReactGA.event({
      category: "Count of couldn't fetch L2",
      label: `L2`,
      action: "Couldn't fetch on L2",
      value: failureCount,
    });
  }, [safeGet(mappedProps, "category.payload.lastUpdatedTimeStamp", null)]);

  const showDetails = useCallback(
    (id, name) => {
      history.push(`/details/metric/${id}`);
      ReactGA.event({
        category: `Metric Clicked on L2 ${safeGet(match, "params.name", "")}`,
        label: `Metric: ${name} on L2`,
        action: "Click from L2",
      });
    },
    [history]
  );

  const closeSearch = useCallback(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = undefined;
    mappedProps.searchReset();
    toggleSearch(false);
  }, []);

  const handleChangeTab = useCallback(({ key }: TabData) => {
    mappedProps.changeTimeTabAppWide(key);
    mappedProps.fetchCategory({
      category: safeGet(match, "params.name"),
      filters: mappedProps.filters,
    });
  }, []);

  const badgeRef = useRef();
  const applyRef = (target) => {
    if (!badgeRef.current) {
      badgeRef.current = target && target.getBoundingClientRect();
    }
  };

  const [isRefreshClicked, setIsRefreshClicked] = useState(0);

  const onRefresh = useCallback(() => {
    if (isRefreshClicked) return;
    setIsRefreshClicked(Date.now());
    setTimeout(() => {
      mappedProps.fetchCategory({
        category: safeGet(match, "params.name"),
        filters: mappedProps.filters,
        fresh: true,
      });
    }, CALL_DELAY);
  }, [isRefreshClicked]);

  useEffect(() => {
    if (
      isRefreshClicked &&
      Date.now() - isRefreshClicked > CALL_DELAY &&
      safeGet(mappedProps, "chartsDataPending") === false
    ) {
      setIsRefreshClicked(0);
    }
  }, [safeGet(mappedProps, "chartsDataPending"), isRefreshClicked]);

  const isFilterApplied = useMemo(
    () => Object.entries(mappedProps.filters).some(([key, value]: [string, any]): boolean => value.length !== 0),
    [mappedProps.filters]
  );

  const onFilterClick = useCallback(() => {
    setFiltersPane(true);
  }, []);

  const onFilterClose = useCallback(() => {
    setFiltersPane(false);
  }, []);

  const onFiltersApply = useCallback((filters: IObject<any>) => {
    onFilterClose();
    mappedProps.setCategoriesFilters(filters);
    mappedProps.fetchCategory({
      category: safeGet(match, "params.name"),
      filters: filters,
    });
  }, []);

  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const searchMetric = useCallback((text: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = undefined;
    if (!text || !text.trim()) {
      mappedProps.searchReset();
      return;
    }
    searchTimerRef.current = setTimeout(() => {
      mappedProps.searchCategory({
        text: text.trim(),
        superCategory: safeGet(match, "params.name", ""),
      });
    }, 300);
  }, [safeGet(match, "params.name", "")]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  const options = useRef<OptionGroupType>([
    {
      className: "option1",
      iconClass: "sync",
      handleClick: onRefresh,
    },
    // {
    //   className: "option2",
    //   iconClass: "sliders",
    //   handleClick: onFilterClick,
    // },
    {
      className: "option3",
      iconClass: "search",
      handleClick: onSearchClick,
    },
  ]);

  const aggregationFields = (mappedProps.filtersConfig.payload?.config?.children || []).map(
    ({ id, meta: { label } }) => ({
      id,
      label,
    })
  );

  const filterSliceId: number =
    (mappedProps.filtersConfig.payload || {}).config?.data?.meta?.queryParams?.form_data?.slice_id || 918;
  const syncIcon = (
    <IconHolder>
      <SyncOutlined />
    </IconHolder>
  );

  // ********** Animations Start ************

  const iconStyles = useSpring(REFRESH_ICON_ANIMATION);

  const slideDownInitialState = { duration: 300, translateY: 0 };
  const [slideDownStyles, slideDownApi] = useSpring(() => slideDownInitialState);

  const refreshBadgeInitialState = {
    translateX: 0,
    translateY: 0,
    opacity: 0.6,
    backgroundColor: "transparent",
    color: colors.paleYellow,
    duration: 300,
  };
  const [refreshBadgeStyles, refreshBadgeApi] = useSpring(() => refreshBadgeInitialState);

  useEffect(() => {
    if (isRefreshClicked) {
      slideDownApi({
        translateY: 30,
      });
      refreshBadgeApi({
        translateX:
          window.innerWidth / 2 - safeGet(badgeRef, "current.width", 0) / 2 - safeGet(badgeRef, "current.left", 0),
        translateY: 20,
        opacity: 1,
        backgroundColor: colors.canaryHighlight,
        color: colors.l2Background,
      });
    } else {
      slideDownApi(slideDownInitialState);
      refreshBadgeApi(refreshBadgeInitialState);
    }
  }, [isRefreshClicked]);

  // ********** Animations End ************

  return (
    <>
      <div className={`${styles["l2-container"]} ${showSearch && styles["display-none"]}`}>
        <div className={styles["static-head-holder"]}>
          <div className={styles["all-metric-top-nav"]}>
            <TopNavbar
              isHome={false}
              handleClick={onBack}
              headerText={mappedProps.header}
              rootStyle={`padding-bottom: 0;`}
            >
              {options.current.map((option) => (
                <MenuItem
                  key={option.iconClass}
                  {...option}
                  showMarker={option.iconClass == "sliders" && isFilterApplied}
                />
              ))}
            </TopNavbar>
            {!!isRefreshClicked && (
              <RefreshBadge style={refreshBadgeStyles} onClick={onRefresh} ref={applyRef}>
                <span>updating</span>
                <animated.div style={iconStyles}>{syncIcon}</animated.div>
              </RefreshBadge>
            )}
          </div>
        </div>
        <animated.div style={slideDownStyles} className={styles["l2-content"]}>
          <div className={styles["all-metric-content"]}>
            {
              safeGet(mappedProps, "category.success", false) || safeGet(mappedProps, "category.pending", false) ? (
                <Tabs
                  tabs={TabNames.map((tabName) => ({ title: tabName, key: tabName }))}
                  page={mappedProps.selectedTab}
                  onChange={handleChangeTab}
                >
                  {TabNames.map((tabName) => (
                    <div key={tabName} className={styles.categoryContainer}>
                      {safeGet(mappedProps, "category.success", false) ? (
                        (() => {
                          const data = safeGet(mappedProps, "category.payload.data", []);
                          return (Array.isArray(data) ? data : []).map((item) => (
                          <div key={item.id} className={styles["all-metric-scroller"]}>
                            <div className={styles["scroller-head"]}>{item.group.toUpperCase()}</div>
                            <NumberScroller
                              charts={Array.isArray(item?.metrics) ? item.metrics : []}
                              showDetails={showDetails}
                              selectedTab={mappedProps.selectedTab}
                            />
                          </div>
                        ));
                        })()
                      ) : (
                        <div className={styles["spinner"]}>
                          <Spin />
                        </div>
                      )}
                    </div>
                  ))}
                </Tabs>
              ) : safeGet(mappedProps, "category.failure", false) ? (
                <div className={styles["error-state"]}>
                  <p>Failed to load metrics</p>
                  <Button size="small" onClick={onRefresh}>Retry</Button>
                </div>
              ) : null
            }
            <div className={styles["right-gradient"]}></div>
          </div>
        </animated.div>
      </div>
      <Search
        closeSearch={closeSearch}
        searchMetric={searchMetric}
        searchResult={safeGet(mappedProps, "search.payload", []) || []}
        open={showSearch}
        searchSuccess={safeGet(mappedProps, "search.success", false)}
        searchPending={safeGet(mappedProps, "search.pending", false)}
        resetSearch={mappedProps.searchReset}
      />
      {showFiltersPane && (
        <FiltersPane
          sliceId={filterSliceId}
          onClose={onFilterClose}
          onApply={onFiltersApply}
          initialValue={mappedProps.filters}
          callerStatus={reduxOperationToStatus(mappedProps.category)}
          aggregationFields={aggregationFields}
        />
      )}
    </>
  );
};

export default Category;
